import type { FC } from "react";
import { useEffect, useState } from "react";
import type { AppLocale } from "@akina/infra";
import {
  addDays,
  addMonths,
  differenceInDays,
  eachMonthOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { Dropdown, Icon, Text } from "../../atoms";

type CalendarProps = {
  setIsOpen: (arg: boolean) => void;
  date: Date | undefined;
  onChange?: (date: Date) => void;
  locale?: AppLocale;
  searchPlaceholder?: string;
}

const createArrayOfNElements = (n: number) => [...Array(n).keys()];

const getFirstDay = (date: Date) => startOfWeek(startOfMonth(date));

const getLastDay = (date: Date) => endOfWeek(endOfMonth(date));

const arrowButtonClasses =
  "w-9 h-9 bg-white hover:bg-gray-800/10 transition flex items-center justify-center rounded-full";

const monthYearButtonClasses = "flex flex-row items-center bg-white hover:bg-gray-800/10 transition px-2 py-1 rounded";

export const Calendar: FC<CalendarProps> = ({ setIsOpen, date, onChange, locale, searchPlaceholder }) => {
  const today = new Date();
  const [focusedIndex, setFocusedIndex] = useState<number>();
  const [displayedDate, setDisplayedDate] = useState(date ? date : today);
  const firstDay = getFirstDay(displayedDate);
  const lastDay = getLastDay(displayedDate);
  const numberOfDays = differenceInDays(lastDay, firstDay) + 1;

  useEffect(() => {
    if (focusedIndex != null) {
      document.getElementById(`day-${focusedIndex}`)?.focus();
    }
  }, [focusedIndex]);

  const MONTHS = eachMonthOfInterval({
    start: new Date(2020, 0, 1),
    end: new Date(2020, 11, 1),
  }).map(d => ({
    label: new Intl.DateTimeFormat(locale, {
      month: "long",
    }).format(d),
    value: d.getMonth(),
  }));

  const YEARS = eachYearOfInterval({
    start: new Date(1930, 0, 1),
    end: new Date(),
  }).map(d => ({
    label: new Intl.DateTimeFormat(locale, {
      year: "numeric",
    }).format(d),
    value: d.getFullYear(),
  }));

  return (
    <div className="flex h-full w-full flex-col">
      <div className="mb-2 flex flex-row items-center justify-between">
        <button className={arrowButtonClasses} onClick={() => setDisplayedDate(d => addMonths(d, -1))}>
          <Icon type="chevronLeft" />
        </button>
        <div className="flex flex-1 justify-center">
          <Dropdown
            withSearch
            withScroll
            onSelect={option => {
              setDisplayedDate(prev => {
                const newDate = new Date(prev.setMonth(option.value));
                onChange && onChange(newDate);
                return newDate;
              });
            }}
            options={MONTHS}
            trigger={
              <button className={`${monthYearButtonClasses} mr-2`}>
                {new Intl.DateTimeFormat(locale, {
                  month: "long",
                }).format(displayedDate)}
                <Icon className="ml-1 w-5" type="chevronDown" />
              </button>
            }
            searchPlaceholder={searchPlaceholder}
          />
          <Dropdown
            withSearch
            withScroll
            onSelect={option => {
              setDisplayedDate(prev => {
                const newDate = new Date(prev.setFullYear(option.value));
                onChange && onChange(newDate);
                return newDate;
              });
            }}
            options={YEARS}
            trigger={
              <button className={`${monthYearButtonClasses} mr-2`}>
                {new Intl.DateTimeFormat(locale, {
                  year: "numeric",
                }).format(displayedDate)}
                <Icon className="ml-1 w-5" type="chevronDown" />
              </button>
            }
            searchPlaceholder={searchPlaceholder}
          />
        </div>

        <button className={arrowButtonClasses} onClick={() => setDisplayedDate(d => addMonths(d, 1))}>
          <Icon type="chevronRight" />
        </button>
      </div>

      <div className="grid grid-cols-7">
        {createArrayOfNElements(7).map(index => (
          <Text fontWeight="font-semibold" className="h-10 p-2 text-center" key={index}>
            {new Intl.DateTimeFormat(locale, {
              weekday: "short",
            }).format(addDays(firstDay, index))}
          </Text>
        ))}
      </div>

      <div className="grid flex-1 grid-cols-7">
        {createArrayOfNElements(numberOfDays).map(index => {
          const day = addDays(firstDay, index);

          const isSelected = date && isSameDay(date, day);
          const isAnotherMonth = !isSameMonth(displayedDate, day);
          const isToday = isSameDay(today, day);
          const defaultClassNames = "h-10 transition rounded m-0.5";
          const classNames = () => {
            if (isSelected) return "bg-primary-100";
            if (isAnotherMonth) return "opacity-30 hover:bg-gray-800/10";
            if (isAnotherMonth) return "opacity-30 hover:bg-gray-800/10";
            return "bg-white hover:bg-gray-800/10";
          };

          const color = () => {
            if (isSelected && isToday) return "text-white";
            if (isSelected) return "text-white";
          };

          const fontWeight = () => {
            if (isSelected && isToday) return "font-semibold";

            if (isToday) return "font-semibold";
          };

          return (
            <button
              className={`${defaultClassNames} ${classNames()}`}
              id={`day-${index}`}
              key={index}
              onKeyDown={e => {
                switch (e.key) {
                  case "ArrowDown":
                    setFocusedIndex(index + 7);
                    break;

                  case "ArrowUp":
                    setFocusedIndex(index - 7);
                    break;

                  case "ArrowRight":
                    setFocusedIndex(index + 1);
                    break;

                  case "ArrowLeft":
                    setFocusedIndex(index - 1);
                    break;
                }
              }}
              onClick={() => {
                if (onChange) onChange(new Date(day));
                setIsOpen(false);
              }}>
              <Text color={color()} fontWeight={fontWeight()}>
                {new Intl.DateTimeFormat(locale, {
                  day: "2-digit",
                }).format(day)}
              </Text>
            </button>
          );
        })}
      </div>
    </div>
  );
};
